import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type ConfidenceLabel = 'HIGH CONFIDENCE' | 'MEDIUM CONFIDENCE' | 'LOW CONFIDENCE';
export type RootCauseReviewState = 'ACCEPTED' | 'DISCARDED' | 'UNREVIEWED';

export type FaultyChangeEntity = {
	id: string;
	type: string;
	attributes: {
		displayName?: string;
		description?: string;
		id: string;
		url: string;
	};
};

export type FaultyChange = FaultyChangeEntity & {
	insights: {
		confidenceScore: number;
		confidenceLabel: ConfidenceLabel;
		reasoning: string;
		summary: string;
		reviewStatus: RootCauseReviewState;
	};
	rcaHypothesisId: string;
	associations?: FaultyChangeEntity[];
};

export type TeamChatChannel = {
	name?: string;
	platform?: string;
	url?: string;
};

export type TeamInfo = {
	chatChannel?: TeamChatChannel;
	id?: string;
	name?: string;
};

export type ServiceOngoingIncident = {
	id: string;
	issueKey: string;
};

export type FaultyService = {
	attributes: {
		name: string;
		ownerTeamInfo?: TeamInfo;
		serviceType?: {
			key: string;
			name: string;
		};
	};
	faultyChanges: FaultyChange[];
	ongoingIncidents: ServiceOngoingIncident[];
	id: string;
};

type InvestigationState =
	| 'NEW'
	| 'FETCHING_SERVICE_DEPENDENCIES'
	| 'FETCHING_DEVOPS_CHANGES'
	| 'ANALYZING_DEVOPS_CHANGES'
	| 'GENERATING_RCA_SUGGESTIONS';

export type StepSectionSummary = {
	type: 'INFO' | 'WARNING';
	text: string;
};

type BaseStep = {
	sectionId: string;
	sectionName: string;
};

export type InvestigationTodoStep = BaseStep & {
	sectionState: 'TODO';
};

export type InvestigationInProgressStep = BaseStep & {
	sectionState: 'IN_PROGRESS';
	sectionSummary: StepSectionSummary[];
	duration?: number;
};

export type InvestigationCompleteStep = BaseStep & {
	sectionState: 'COMPLETE';
	sectionSummary: StepSectionSummary[];
	duration?: number;
};

export type ThinkingStep =
	| InvestigationTodoStep
	| InvestigationInProgressStep
	| InvestigationCompleteStep;

export type ProcessedEntityType = 'DEPLOYMENT' | 'COMMIT' | 'FF';

export type ProcessedEntitiesCount = Partial<Record<ProcessedEntityType, number>>;

export type ChangeCandidateFunnelCounts = {
	initial: number;
	afterCorrelationFilter: number;
	afterReasoningFilter: number;
	afterReviewerFilter: number;
};

export type Investigation = {
	faultyServices: FaultyService[];
	investigationId: string;
	investigationMetadata: {
		problemContextSummary: string;
		sourceEntityId: string;
		sourceEntityType: string;
		affectedServices: string[];
		processedEntitiesCount?: ProcessedEntitiesCount;
		changeCandidateFunnelCounts?: ChangeCandidateFunnelCounts;
		noRCARationale?: string;
		createdBy: string;
		terminatedBy: string;
		status: { currentState: 'COMPLETE' | 'TERMINATED'; isComplete: true; lastUpdatedAt: number };
	};
	thinkingSteps: ThinkingStep[];
};

export type PartialInvestigation = {
	investigationId: string;
	investigationMetadata: {
		sourceEntityId: string;
		sourceEntityType: string;
		affectedServices: string[];
		createdBy: string;
		status: {
			currentState: InvestigationState;
			isComplete: false;
			progress: {
				stepsCompleted: number;
			};
		};
	};
	thinkingSteps: ThinkingStep[];
};

export type TerminatedInvestigation = Omit<PartialInvestigation, 'investigationMetadata'> & {
	investigationMetadata: Omit<PartialInvestigation['investigationMetadata'], 'status'> & {
		terminatedBy: string;
		status: {
			currentState: 'TERMINATED';
			isComplete: true;
		};
	};
};

export type InvestigationData = Investigation | PartialInvestigation | TerminatedInvestigation;

type InvestigationResponseError = {
	code: number;
	message: string;
};

type InvestigationResponse = {
	data?: InvestigationData;
	error?: InvestigationResponseError;
};

export const getRcaInvestigationData = (
	cloudId: string,
	investigationId: string,
	signal?: AbortSignal,
	includeThinkingSteps: boolean = true,
): Promise<InvestigationData> =>
	performGetRequest<InvestigationResponse>(
		`/gateway/api/jsm/aiops/cloudId/${cloudId}/api/v1/investigation?investigation_id=${investigationId}${fg('rca_investigation_exclude_extra_fields') ? `&include_audit_logs=false${includeThinkingSteps ? '' : '&include_thinking_steps=false'}` : ''}`,
		{
			signal,
		},
	).then((response) => {
		if (response.error) {
			throw new FetchError(response.error.code, response.error.message);
		}

		if (!response.data) {
			throw new Error(
				`getRcaInvestigationData: investigation response missing data for investigation ID: ${investigationId}`,
			);
		}

		return response.data;
	});
